import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout, Menu, Form, Input, Button, Table, Radio, Upload, message, Modal, Select } from 'antd';
import styles from './itMember.module.less';
import Gstyle from './index.module.less';
import layoutStyles from './sharePageLayout.module.less';
import useTableScrollY from './useTableScrollY';
import Company from '@/asset/new/company.svg';
import closeIcon from '@/asset/new/Close.png';
import editIcon from '@/asset/new/edit.png'; // 引入编辑图标
import dayjs from 'dayjs';
import {
    knowledgeMemberList,
    knowledgeDictionaryList,
    knowledgeDictionaryUpdate,
    knowledgeMemberImport,
    knowledgeMemberTaTagImport,
    knowledgeTaTagList,
    knowledgePermissionTaUpdate,
} from '@/api/knowledge';
import { API_ROOT } from '@/env';
import { downloadExportFile } from './downloadExport';
import excelIcon from '@/asset/new/excel.png';
const { Sider, Content, Header } = Layout;
const { SubMenu } = Menu;

const BU_MENU_ROOT_KEY = 'bu-abbvie-root';
/** 成员导入弹窗类型：邮箱/LM | TA-Tag */
const IMPORT_MODAL_TYPE = {
    EMAIL_LM: 'emailLm',
    TA_TAG: 'taTag',
};
/** 分享链接有效期预设选项 */
const EXPIRES_PRESET_VALUES = ['3', '7', '30', '99999999'];
const EXPIRES_DICT_TYPE = 'expires_date';

function normalizeDictRows(res) {
    if (!res || res.status !== 200 || res.data == null) {
        return [];
    }
    const raw = res.data;
    if (Array.isArray(raw)) {
        return raw;
    }
    return raw.records ?? raw.list ?? raw.rows ?? [];
}

/** TA Tag 列表 → 下拉 options（value 为 id） */
function mapTaTagListToOptions(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map(item => ({
        label: item.tagName || item.tagType || String(item.id),
        value: item.id,
    }));
}

/** 按 BU 拉取启用中的 TA Tag 下拉（status=1） */
async function fetchEnabledTaTagOptionsByBu(buId) {
    if (buId == null || buId === '') {
        return [];
    }
    try {
        const res = await knowledgeTaTagList({
            pageNo: 1,
            pageSize: 9999,
            buId,
            searchType: 0,
            status: 1,
        });
        if (res.status === 200 && res.data != null) {
            const data = res.data;
            const records = data.records ?? data.list ?? data.rows ?? [];
            const enabled = Array.isArray(records)
                ? records.filter(item => item.status === 1)
                : [];
            return mapTaTagListToOptions(enabled);
        }
        return [];
    } catch {
        return [];
    }
}

/** 字典 BU → 侧栏菜单（一级艾伯维，子项为 dictLabel / dictValue） */
function buildBuSideMenu(rows) {
    const list = Array.isArray(rows) ? rows : [];
    return [
        {
            key: BU_MENU_ROOT_KEY,
            label: '艾伯维',
            children: list.map(item => ({
                key: String(item.id),
                label: item.dictLabel,
            })),
            icon: <img src={Company} style={{ width: '24px', height: '24px' }} alt="icon" />,
        },
    ];
}

const ITMember = () => {
    const [editForm] = Form.useForm();
    const [menuData, setMenuData] = useState([]);
    const [buDictReady, setBuDictReady] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [openKeys, setOpenKeys] = useState([BU_MENU_ROOT_KEY]);
    const [dataSource, setDataSource] = useState([]);
    const [total, setTotal] = useState(0);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [name, setName] = useState('');
    const [upi, setUpi] = useState('');
    /** 点击「查询」后生效的筛选条件 */
    const [listFilters, setListFilters] = useState({});
    const [tabType, setTabType] = useState('itMemberManagement');
    const [days, setDays] = useState('3');
    const [customDays, setCustomDays] = useState('');
    const [expiresDictRecord, setExpiresDictRecord] = useState(null);
    const [shareSettingsLoading, setShareSettingsLoading] = useState(false);
    const [shareSettingsSaving, setShareSettingsSaving] = useState(false);
    const [fileModalVisible, setFileModalVisible] = useState(false);
    const [importModalType, setImportModalType] = useState(IMPORT_MODAL_TYPE.EMAIL_LM);
    /** 批量导入弹窗内 Upload 受控列表（仅 1 个文件） */
    const [batchImportFileList, setBatchImportFileList] = useState([]);
    const [importResultModalVisible, setImportResultModalVisible] = useState(false);
    const [importResultMessage, setImportResultMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [editModal, setEditModal] = useState({ visible: false, record: null });
    const [editTaTagOptions, setEditTaTagOptions] = useState([]);
    const [editTaTagLoading, setEditTaTagLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const { tableWrapRef, tableScrollY } = useTableScrollY([
        dataSource.length,
        total,
        pageSize,
        tabType,
    ]);
    /** 当前选中 BU 在菜单上的展示名（selectedKeys 仅为 key 字符串，不是字典对象） */
    const selectedBuLabel = useMemo(() => {
        const key = selectedKeys[0];
        if (!key || String(key) === BU_MENU_ROOT_KEY) {
            return '';
        }
        const children = menuData[0]?.children;
        if (!Array.isArray(children)) {
            return '';
        }
        const hit = children.find(c => String(c.key) === String(key));
        return hit?.label ?? '';
    }, [menuData, selectedKeys]);

    /** 点击最外层「艾伯维」标题时不收起，始终保持该分组展开 */
    const onOpenChange = keys => {
        if (!keys.includes(BU_MENU_ROOT_KEY)) {
            setOpenKeys([BU_MENU_ROOT_KEY]);
            return;
        }
        setOpenKeys(keys);
    };
    // 动态递归渲染菜单（文案超长省略，悬停 title 显示全文）
    const renderMenuLabelText = text => {
        const s = text != null ? String(text) : '';
        return (
            <span className={styles.sideMenuLabelEllipsis} title={s}>
                {s}
            </span>
        );
    };
    const renderMenu = data => {
        return data.map(item => {
            if (item.children && item.children.length > 0) {
                return (
                    <SubMenu key={item.key} title={renderMenuLabelText(item.label)} icon={item.icon}>
                        {renderMenu(item.children)}
                    </SubMenu>
                );
            }
            return <Menu.Item key={item.key}>{renderMenuLabelText(item.label)}</Menu.Item>;
        });
    };
    // 表格列配置
    const columns = [
        {
            title: 'BU',
            dataIndex: 'bu',
            key: 'bu',
            width: 100,
        },
        // { title: '部门', dataIndex: 'departName', key: 'departName', width: 100 },
        { title: '姓名', dataIndex: 'name', key: 'name', width: 100, ellipsis: true },
        { title: 'UPI', dataIndex: 'upi', key: 'upi', width: 100 },
        { title: '职务', dataIndex: 'position', key: 'position', width: 100 },
        { title: 'TA TAG', dataIndex: 'taTagName', key: 'taTagName', width: 160 },
        { title: '发送邮箱', dataIndex: 'sendEmail', key: 'sendEmail', width: 130 },
        { title: '企微邮箱', dataIndex: 'email', key: 'email', width: 130 },
        { title: '导入人', dataIndex: 'creatorName', key: 'creatorName', width: 100 },
        {
            title: '导入时间',
            dataIndex: 'modifyTime',
            key: 'modifyTime',
            width: 100,
            render: text => {
                if (!text) return ' ';
                return dayjs(text).format('YYYY-MM-DD');
            },
        },
        {
            title: '上级经理', dataIndex: 'superiorName', key: 'superiorName', width: 100, render: (_, record) => {
                const name = record?.superiorName ?? '';
                const status = record.onjob ? '在职' : '离职';
                return name ? `${name} (${status})` : "";
            },
        },
        { title: '上级经理UPI', dataIndex: 'superiorUpi', key: 'superiorUpi', width: 100 },
        // {
        //     title: '操作',
        //     key: 'action',
        //     width: 80,
        //     align: 'center',
        //     render: (_, record) => (
        //         <img
        //             src={editIcon}
        //             alt="编辑"
        //             title="编辑"
        //             className={styles.editIcon}
        //             onClick={() => openEditModal(record)}
        //         />
        //     ),
        // }
    ];
    // 列表数据
    const fetchData = useCallback(
        async (opts = {}) => {
            const pageNo = opts.pageNo != null ? opts.pageNo : current;
            const buId = selectedKeys[0];
            const payload = {
                pageNo,
                pageSize: pageSize,
                ...(buId ? { buId } : {}),
            };
            if (listFilters.name && String(listFilters.name).trim()) {
                payload.params = String(listFilters.name).trim();
            }
            if (listFilters.upi && String(listFilters.upi).trim()) {
                payload.upi = String(listFilters.upi).trim();
            }
            const res = await knowledgeMemberList(payload);
            if (res.status === 200) {
                const data = res.data;
                setDataSource(data.records);
                setTotal(data.total);
            }
        },
        [current, listFilters, pageSize, selectedKeys]
    );
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await knowledgeDictionaryList({ dictType: 'BU' });
                if (cancelled) {
                    return;
                }
                if (res.status !== 200) {
                    setMenuData([]);
                    setBuDictReady(true);
                    return;
                }
                const rows = normalizeDictRows(res);
                const built = buildBuSideMenu(rows);
                setMenuData(built);
                const firstChild = built[0]?.children?.[0];
                if (firstChild) {
                    setSelectedKeys([String(firstChild.key)]);
                    setOpenKeys([BU_MENU_ROOT_KEY]);
                }
            } catch {
                if (!cancelled) {
                    setMenuData([]);
                }
            } finally {
                if (!cancelled) {
                    setBuDictReady(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // BU 字典就绪后再拉列表；切换 BU / 分页 / 查询条件变化时刷新
    useEffect(() => {
        if (tabType !== 'itMemberManagement') {
            return;
        }
        if (!buDictReady) {
            return;
        }
        fetchData();
    }, [fetchData, tabType, buDictReady, selectedKeys]);

    /** 加载分享链接有效期字典 */
    const fetchExpiresSettings = useCallback(async () => {
        setShareSettingsLoading(true);
        try {
            const res = await knowledgeDictionaryList({ dictType: EXPIRES_DICT_TYPE });
            if (res.status !== 200) {
                return;
            }
            const rows = normalizeDictRows(res);
            const record = rows[0];
            if (!record) {
                setExpiresDictRecord(null);
                return;
            }
            setExpiresDictRecord(record);
            const value = String(record.dictValue ?? '');
            if (EXPIRES_PRESET_VALUES.includes(value)) {
                setDays(value);
                setCustomDays('');
            } else if (value) {
                setDays('custom');
                setCustomDays(value);
            }
        } catch {
            message.error('分享设置加载失败');
        } finally {
            setShareSettingsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (tabType === 'shareSettings') {
            fetchExpiresSettings();
        }
    }, [tabType, fetchExpiresSettings]);

    const onSaveShareSettings = async () => {
        if (!expiresDictRecord?.id) {
            message.warning('未找到分享设置配置，无法保存');
            return;
        }
        let dictValue = days;
        if (days === 'custom') {
            const trimmed = String(customDays ?? '').trim();
            if (!trimmed || !/^\d+$/.test(trimmed) || Number(trimmed) <= 0) {
                message.warning('请输入有效的自定义天数');
                return;
            }
            dictValue = trimmed;
        }
        setShareSettingsSaving(true);
        try {
            const res = await knowledgeDictionaryUpdate({
                id: expiresDictRecord.id,
                dictLabel: dictValue,
                dictValue,
                dictType: EXPIRES_DICT_TYPE,
                remark: expiresDictRecord.remark || '分享链接有效期',
            });
            if (res.status === 200 || res.success === true) {
                message.success('保存成功');
                setExpiresDictRecord(prev =>
                    prev ? { ...prev, dictLabel: dictValue, dictValue } : prev
                );
                if (days === 'custom') {
                    setCustomDays(dictValue);
                }
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (e) {
            message.error(e?.message || '保存失败');
        } finally {
            setShareSettingsSaving(false);
        }
    };

    const onQuery = () => {
        const next = {};
        if (name && String(name).trim()) {
            next.name = String(name).trim();
        }
        if (upi && String(upi).trim()) {
            next.upi = String(upi).trim();
        }
        setListFilters(next);
        setCurrent(1);
    };

    /** 下载导入模版（Excel 流） */
    const onDownloadTemplate = () => {
        const bu = selectedKeys[0];
        const query = bu ? `?bu=${encodeURIComponent(bu)}` : '';
        const isTaTagImport = importModalType === IMPORT_MODAL_TYPE.TA_TAG;
        downloadExportFile({
            url: isTaTagImport
                ? `${API_ROOT}/knowledge/member/ta-tag/template${query}`
                : `${API_ROOT}/knowledge/member/template${query}`,
            method: 'get',
            fileName: isTaTagImport
                ? `TA-Tag导入模版_${dayjs().format('YYYYMMDD')}.xlsx`
                : `IT成员导入模版_${dayjs().format('YYYYMMDD')}.xlsx`,
            errorMessage: '模版下载失败',
        });
    };

    /** 解码 Base64 UTF-8 文本并触发浏览器下载 */
    const downloadImportReportFile = (base64, fileName) => {
        if (!base64 || !String(base64).trim()) {
            return;
        }
        try {
            const binary = atob(String(base64).trim());
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) {
                bytes[i] = binary.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'text/plain;charset=utf-8' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download =
                fileName && String(fileName).trim()
                    ? String(fileName).trim()
                    : `IT成员导入结果_${dayjs().format('YYYYMMDD_HHmmss')}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch {
            message.error('结果文件下载失败');
        }
    };

    /** 批量导入 Excel，成功返回结果对象 */
    const runBatchImport = async (file, type = IMPORT_MODAL_TYPE.EMAIL_LM) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const importApi =
                type === IMPORT_MODAL_TYPE.TA_TAG
                    ? knowledgeMemberTaTagImport
                    : knowledgeMemberImport;
            const res = await importApi(formData);
            if (res && (res.success === true || res.status === 200)) {
                const payload = res.data || {};
                const tipMessage =
                    payload.message || res.message || '导入完成';
                const reportFileName = payload.reportFileName;
                const reportFileBase64 = payload.reportFileBase64;
                setCurrent(1);
                fetchData({ pageNo: 1 });
                return {
                    ok: true,
                    message: tipMessage,
                    reportFileName,
                    reportFileBase64,
                };
            }
            return { ok: false, message: res?.message || '导入失败' };
        } catch (e) {
            message.error(e?.message || '导入失败');
            return { ok: false, message: e?.message || '导入失败' };
        }
    };

    const openBatchImportModal = (type = IMPORT_MODAL_TYPE.EMAIL_LM) => {
        setImportModalType(type);
        setBatchImportFileList([]);
        setFileModalVisible(true);
    };

    const closeBatchImportModal = () => {
        setFileModalVisible(false);
        setBatchImportFileList([]);
    };

    const handleModalBatchUpload = async () => {
        const item = batchImportFileList[0];
        const file = item?.originFileObj ?? item;
        if (!file || typeof file.name !== 'string') {
            message.warning('请先选择文件');
            return;
        }
        setLoading(true);
        try {
            const result = await runBatchImport(file, importModalType);
            closeBatchImportModal();
            if (result?.ok) {
                if (result.reportFileBase64) {
                    downloadImportReportFile(
                        result.reportFileBase64,
                        result.reportFileName,
                    );
                }
                setImportResultMessage(result.message || '导入完成');
                setImportResultModalVisible(true);
            } else {
                setImportResultMessage(result?.message || '导入失败');
                setImportResultModalVisible(true);
            }
        } finally {
            setLoading(false);
        }
    };
    const closeImportResultModal = () => {
        setImportResultModalVisible(false);
        setImportResultMessage('');
    };
    const onImport = () => {
        downloadExportFile({
            url: `${API_ROOT}/knowledge/member/export`,
            method: 'get',
            fileName: `IT成员_${dayjs().format('YYYYMMDD')}.xlsx`,
        });
    };

    const openEditModal = async record => {
        const buId = selectedKeys[0];
        if (!buId) {
            message.warning('请先选择 BU');
            return;
        }
        if (!record?.klsTaUserLink?.id) {
            message.warning('该成员暂无 TA Tag 关联，无法编辑');
            return;
        }
        setEditModal({ visible: true, record });
        editForm.setFieldsValue({
            taTag: record.klsTaUserLink.taId || record.taTag,
        });
        setEditTaTagLoading(true);
        try {
            const options = await fetchEnabledTaTagOptionsByBu(buId);
            setEditTaTagOptions(options);
        } finally {
            setEditTaTagLoading(false);
        }
    };

    const closeEditModal = () => {
        setEditModal({ visible: false, record: null });
        editForm.resetFields();
        setEditTaTagOptions([]);
    };

    const onConfirmEdit = async () => {
        const record = editModal.record;
        const link = record?.klsTaUserLink;
        if (!link?.id) {
            message.warning('该成员暂无 TA Tag 关联，无法编辑');
            return;
        }
        try {
            const values = await editForm.validateFields();
            const selected = editTaTagOptions.find(
                item => String(item.value) === String(values.taTag),
            );
            setEditSaving(true);
            const res = await knowledgePermissionTaUpdate({
                deleted: link.deleted ?? 0,
                createTime: link.createTime,
                creatorName: link.creatorName,
                creatorId: link.creatorId,
                modifyTime: link.modifyTime,
                modifierName: link.modifierName,
                modifierId: link.modifierId,
                id: link.id,
                userId: link.userId,
                taId: values.taTag,
                ta: selected?.label ?? link.ta ?? '',
            });
            if (res && (res.status === 200 || res.success === true)) {
                message.success('修改成功');
                closeEditModal();
                fetchData();
            }
        } catch (e) {
            if (e?.errorFields) {
                return;
            }
            message.error(e?.message || '修改失败');
        } finally {
            setEditSaving(false);
        }
    };

    return (
        <>
            <style>{`
              /* 最外层父级标题：颜色 #071D49 加粗 */
	                .custom-menu > .ant-menu-submenu > .ant-menu-submenu-title {
	                    color: #000000 !important;
	                    font-weight: bold !important;
                        font-size: 18px !important;
                        height: 80px !important;
                        display: flex;
                        align-items: center;
	                }
	                /* 未选中的子菜单 Item：颜色 */
	                .custom-menu .ant-menu-item {
	                    color: #071D49 !important;
	                }
	                /* 选中的子菜单 Item：颜色 #071D49，背景色 rgba(0, 102, 245, 0.15) */
	                .custom-menu .ant-menu-item-selected {
	                    color: #071D49 !important;
	                    background-color: rgba(0, 102, 245, 0.15) !important;
	                }
	                /* 修复 antd 默认选中时的左侧高亮条 */
                    
	                // .custom-menu .ant-menu-item-selected::after,.ant-menu-inline .ant-menu-item::after {
	                //     border-right: 3px solid #071D49 !important;
	                //     left: 0 !important;
	                //     right: auto;
	                // }
                        .custom-menu .ant-menu-inline .ant-menu-item::after {
                        border-right: 3px solid #071D49 !important;
                        left: 0 !important;
                        right: auto;
                    }
	                .ant-menu-submenu-arrow {
	                    color: #071D49 !important;
	                }
                    /* 侧栏宽度内省略：父级标题与子项均限制溢出 */
                    .custom-menu.ant-menu-inline .ant-menu-item {
                        overflow: hidden;
                    }
                    .custom-menu .ant-menu-submenu-title {
                        overflow: hidden;
                    }

	            `}</style>
            <Layout style={{ height: '100vh', background: '#EBEDF9' }}>
                {tabType === 'itMemberManagement' && (
                    <Sider
                        width={180}
                        style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}
                    >
                        <Menu
                            className="custom-menu"
                            mode="inline"
                            selectedKeys={selectedKeys}
                            openKeys={openKeys}
                            onOpenChange={onOpenChange}
                            onSelect={({ key }) => setSelectedKeys([key])}
                            expandIcon={() => null}
                            style={{
                                height: '100vh',
                                borderRight: 0,
                                overflowY: 'auto',
                                background: '#ffffff',
                            }}
                        >
                            {renderMenu(menuData)}
                        </Menu>
                    </Sider>
                )}

                <Layout>
                    <Header style={{ background: 'none', height: 'auto', }}>
                        <div className={styles.divheader}>
                            <Radio.Group
                                value={tabType}
                                buttonStyle="solid"
                                onChange={e => setTabType(e.target.value)}
                                style={{ lineHeight: 'none', display: 'flex', justifyContent: 'center', margin: '20px 0' }}
                            >
                                <Radio.Button
                                    value="itMemberManagement"
                                    className={styles.divheaderButton}
                                >
                                    IT成员管理
                                </Radio.Button>
                                <Radio.Button
                                    value="shareSettings"
                                    className={styles.divheaderButton}
                                >
                                    分享设置
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                    </Header>
                    <Content className={`${Gstyle.content} ${layoutStyles.pageContent} ${styles.itMemberContent}`}>
                        {tabType === 'itMemberManagement' ? (
                            <>
                                <div className={styles.contentHeaderTitle}>
                                    艾伯维-{selectedBuLabel || '—'}（{total}人）
                                </div>
                                <Form
                                    layout="inline"
                                    className={`${layoutStyles.inlineFilterForm} ${styles.memberToolbar}`}
                                    style={{ marginBottom: 20, flexShrink: 0 }}
                                >
                                    <div className={styles.memberToolbarLeft}>
                                        <Form.Item label="姓名">
                                            <Input
                                                placeholder="请输入"
                                                style={{ width: 170 }}
                                                value={name}
                                                onChange={e => {
                                                    const v = e.target.value;
                                                    setName(v);
                                                }}
                                                allowClear
                                            />
                                        </Form.Item>
                                        <Form.Item label="UPI">
                                            <Input
                                                placeholder="请输入"
                                                style={{ width: 170 }}
                                                value={upi}
                                                onChange={e => {
                                                    const v = e.target.value;
                                                    setUpi(v);
                                                }}
                                                allowClear
                                            />
                                        </Form.Item>
                                        <Form.Item>
                                            <Button type="primary" onClick={onQuery}>
                                                查询
                                            </Button>
                                        </Form.Item>
                                    </div>
                                    <div className={styles.memberToolbarRight}>
                                        <Form.Item>
                                            {/* <Button
                                                type="primary"
                                                style={{ marginRight: 30 }}
                                                onClick={() =>
                                                    openBatchImportModal(IMPORT_MODAL_TYPE.TA_TAG)
                                                }
                                            >
                                                TA-Tag导入
                                            </Button> */}
                                            <Button
                                                type="primary"
                                                style={{ marginRight: 30 }}
                                                onClick={() =>
                                                    openBatchImportModal(IMPORT_MODAL_TYPE.EMAIL_LM)
                                                }
                                            >
                                                邮箱/LM导入
                                            </Button>
                                            <Button type="primary" onClick={onImport}>
                                                导出数据
                                            </Button>
                                        </Form.Item>
                                    </div>
                                </Form>
                                <div ref={tableWrapRef} className={layoutStyles.tableWrap}>
                                    <Table
                                        scroll={
                                            tableScrollY != null ? { y: tableScrollY } : undefined
                                        }
                                        className={`${Gstyle.table} ${styles.itMemberTable}`}
                                        columns={columns}
                                        dataSource={dataSource}
                                        pagination={{
                                            total: total,
                                            pageSize: pageSize,
                                            current: current,
                                            showTotal: total => `共 ${total} 条`,
                                            showQuickJumper: true,
                                            showSizeChanger: true,
                                            onChange: (page, size) => {
                                                setCurrent(page);
                                                setPageSize(size);
                                            },
                                        }}
                                        rowClassName={(record, index) => {
                                            const isMultipleOfTwo = (index + 1) % 2 === 0;
                                            return isMultipleOfTwo ? `${Gstyle.tableRow}` : '';
                                        }}
                                        rowKey={record => record.userId}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.contentHeaderTitle}>
                                    <div>外部分享设置</div>
                                    <Button
                                        type="primary"
                                        loading={shareSettingsSaving}
                                        disabled={shareSettingsLoading}
                                        onClick={onSaveShareSettings}
                                    >
                                        保存
                                    </Button>
                                </div>
                                <Form.Item label="分享链接安全设置">
                                    <Radio.Group
                                        name="radiogroup"
                                        value={days}
                                        disabled={shareSettingsLoading}
                                        onChange={e => setDays(e.target.value)}
                                    >
                                        <Radio value="3">3天</Radio>
                                        <Radio value="7">7天</Radio>
                                        <Radio value="30">30天</Radio>
                                        <Radio value="99999999">永久</Radio>
                                        <Radio value="custom">
                                            自定义
                                            {days === 'custom' && (
                                                <>
                                                    <Input
                                                        placeholder="请输入"
                                                        value={customDays}
                                                        disabled={shareSettingsLoading}
                                                        onChange={e => setCustomDays(e.target.value)}
                                                        style={{
                                                            width: 170,
                                                            marginLeft: 8,
                                                            height: 32,
                                                        }}
                                                    />
                                                    <span style={{ marginLeft: 10 }}>天</span>
                                                </>
                                            )}
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </>
                        )}
                    </Content>
                </Layout>
            </Layout>
            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>
                            {importModalType === IMPORT_MODAL_TYPE.TA_TAG
                                ? 'TA-Tag导入'
                                : '邮箱/LM导入'}
                        </span>
                    </div>
                }
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={fileModalVisible}
                onCancel={closeBatchImportModal}
                width={410}
                destroyOnClose
                footer={
                    <div className={Gstyle.custommodalfooter}>
                        <Button className={Gstyle.mythemebutton} ghost onClick={closeBatchImportModal}>
                            取消
                        </Button>
                        <Button type="primary" onClick={handleModalBatchUpload} loading={loading}>
                            上传
                        </Button>
                    </div>
                }
            >
                <div className={styles.batchImportModal}>
                    <div className={styles.batchImportUploadRow}>
                        <span className={styles.batchImportLabel}>
                            <span className={styles.batchImportRequired}>*</span>
                            上传文件
                        </span>
                        <div className={styles.batchImportUploadMain}>
                            <Upload
                                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                fileList={batchImportFileList}
                                maxCount={1}
                                listType="text"
                                showUploadList={{
                                    showPreviewIcon: false,
                                    showRemoveIcon: true,
                                }}
                                onChange={({ fileList }) => {
                                    setBatchImportFileList(fileList.slice(0, 1));
                                }}
                                beforeUpload={file => {
                                    const max = 50 * 1024 * 1024;
                                    const parts = file.name.split('.');
                                    const ext =
                                        parts.length > 1 ? parts.pop().toLowerCase() : '';
                                    if (!['xlsx', 'xls'].includes(ext)) {
                                        message.warning('仅支持上传 .xlsx、.xls 文件');
                                        return Upload.LIST_IGNORE;
                                    }
                                    if (file.size > max) {
                                        message.warning('附件大小不超过50M');
                                        return Upload.LIST_IGNORE;
                                    }
                                    return false;
                                }}
                            >
                                <Button type="primary" style={{ borderRadius: 4 }}>
                                    选择文件
                                </Button>
                                {batchImportFileList.length === 0 && (
                                    <span className={styles.batchImportFileName}>未选择文件</span>
                                )}
                            </Upload>

                            <div className={styles.batchImportExtra}>附件大小不超过50M</div>
                        </div>
                    </div>
                    <div className={styles.batchImportTemplateBar}>
                        <div className={styles.batchImportTemplateLeft}>
                            <img src={excelIcon} alt="excel" style={{ width: 14, height: 14 }} />
                            <span>
                                {importModalType === IMPORT_MODAL_TYPE.TA_TAG
                                    ? 'TA-Tag导入模板.Excel'
                                    : '邮箱/LM导入模板.Excel'}
                            </span>
                        </div>
                        <Button type="primary" ghost onClick={onDownloadTemplate}>
                            下载
                        </Button>
                    </div>
                </div>
            </Modal>
            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>编辑Tag</span>
                    </div>
                }
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={editModal.visible}
                onCancel={closeEditModal}
                width={410}
                destroyOnClose
                footer={
                    <div className={Gstyle.custommodalfooter}>
                        <Button className={Gstyle.mythemebutton} ghost onClick={closeEditModal}>
                            取消
                        </Button>
                        <Button type="primary" loading={editSaving} onClick={onConfirmEdit}>
                            确认
                        </Button>
                    </div>
                }
            >
                <Form
                    form={editForm}
                    layout="horizontal"
                    className={styles.editFormLabel}
                    requiredMark
                >
                    <Form.Item label="姓名">
                        <span>{editModal.record?.name || ' '}</span>
                    </Form.Item>
                    <Form.Item label="UPI">
                        <span>{editModal.record?.upi || ' '}</span>
                    </Form.Item>
                    <Form.Item
                        label="TA TAG"
                        name="taTag"
                        rules={[{ required: true, message: '请选择 TA TAG' }]}
                        extra={<span className={styles.taTagTip}>注：停用Tag不可见</span>}
                    >
                        <Select
                            placeholder="请选择"
                            options={editTaTagOptions}
                            loading={editTaTagLoading}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>导入结果提示</span>
                    </div>
                }
                zIndex={1001}
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={importResultModalVisible}
                onCancel={closeImportResultModal}
                width={510}
                footer={
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type="primary"
                            onClick={closeImportResultModal}
                            style={{
                                width: '170px',
                                height: '40px',
                                borderRadius: '6px',
                            }}
                        >
                            确定
                        </Button>
                    </div>
                }
            >
                <p
                    className={Gstyle.errorModalContent}
                    style={{
                        height: 'auto',
                        maxHeight: 340,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        padding: '0 8px',
                    }}
                >
                    {importResultMessage}
                </p>
            </Modal>
        </>
    );
};
export default ITMember;
